import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import "@/App.css";

import { CartProvider } from "./lib/cart-context";
import { AuthProvider, useAuth } from "./lib/auth-context";
import { FavoritesProvider } from "./lib/favorites-context";
import { SettingsProvider } from "./lib/settings-context";
import Header from "./components/Header";
import MegaCategoriesPanel from "./components/MegaCategoriesPanel";
import CartDrawer from "./components/CartDrawer";
import WhatsAppFab from "./components/WhatsAppFab";

import Home from "./pages/Home";
import Tienda from "./pages/Tienda";
import CategoryPage from "./pages/Category";
import ProductPage from "./pages/Product";
import SearchPage from "./pages/Search";
import LoginPage from "./pages/Login";
import RegisterPage from "./pages/Register";
import CheckoutPage from "./pages/Checkout";
import CheckoutSuccessPage from "./pages/CheckoutSuccess";
import CartPage from "./pages/Cart";
import OrderConfirmationPage from "./pages/OrderConfirmation";
import AccountPage from "./pages/Account";
import MyOrdersPage from "./pages/MyOrders";
import FavoritesPage from "./pages/Favorites";
import NotFoundPage from "./pages/NotFound";
import EnviarPaqueteNuevoPage from "./pages/EnviarPaqueteNuevo";
import EnviarPaqueteSuccessPage from "./pages/EnviarPaqueteSuccess";
import DeliverPage, { DeliverThanks } from "./pages/DeliverPage";
import DeleteAccountPage from "./pages/DeleteAccount";
import EnvioConfirmacionPage from "./pages/EnvioConfirmacion";
import MisEnviosPage from "./pages/MisEnvios";
import AdminRoutes from "./pages/admin/AdminRoutes";
import LegalPage from "./pages/LegalPage";
import FaqsPublic from "./pages/FaqsPublic";
import AboutPage from "./pages/About";
import ShippingTermsPage from "./pages/ShippingTerms";
import ShoppingListPage from "./pages/ShoppingListPage";
import OlvideContrasenaPage from "./pages/OlvideContrasena";
import RestablecerContrasenaPage from "./pages/RestablecerContrasena";
// Phase 30 — dedicated full-listing pages for the home vertical grid sections
import DestacadosPage from "./pages/Destacados";
import NovedadesPage from "./pages/Novedades";
import ParaTiPage from "./pages/ParaTi";
// Phase 32 — storefront split (Envío / Local). No splash: `/` redirects to
// the visitor's last-visited shop (cookie) or "/envio/" by default.
import { ShopProvider, DEFAULT_SHOP, getShopCookie, SHOP_META } from "./lib/shop-context";
import LegacyShopRedirect from "./components/LegacyShopRedirect";
import LocalToEnvioRedirect from "./components/LocalToEnvioRedirect";
import LegacyOrderRedirect from "./components/LegacyOrderRedirect";
import LegacyEnvioIdRedirect from "./components/LegacyEnvioIdRedirect";
import useContent from "./lib/content";

/** Phase 32 — `/` redirect. Reads the `mercago_shop` cookie; falls back to
 *  the default shop (envio) when there's no cookie. Renders no UI. */
function RootShopRedirect() {
  const cookieShop = getShopCookie();
  const target = SHOP_META[cookieShop] || SHOP_META[DEFAULT_SHOP];
  return <Navigate to={`${target.prefix}/`} replace />;
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-16">
        <div className="h-32 rounded-2xl bg-neutral-100 animate-pulse" />
      </div>
    );
  }
  if (!user) {
    // Preserve destination path via ?redirect= for Login to consume.
    const target = location.pathname + location.search;
    const reason = location.pathname.startsWith("/enviar-paquete")
      ? "ship"
      : "shop";
    return (
      <Navigate
        to={`/entrar?redirect=${encodeURIComponent(target)}&reason=${reason}`}
        replace
        state={{ from: target }}
      />
    );
  }
  return children;
}

function ScrollToTopOnRouteChange() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Only react to pathname changes (hash-only navigation, e.g. "#novedades",
    // and same-route wizard step changes do NOT alter pathname, so those keep
    // their own scroll behavior).
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
}

function Footer() {
  // Fase 4 · CMS — footer dinámico. Los campos vienen del endpoint público
  // `/api/content/footer` y caen en defaults si aún no está sembrado.
  const { fields } = useContent("footer");
  const socials = Array.isArray(fields?.social_links) ? fields.social_links : [];
  return (
    <footer className="mt-16 border-t border-neutral-200 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 text-sm">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 font-black text-lg">
              <div className="h-8 w-8 rounded-lg bg-[#00953B] text-white grid place-items-center">
                M
              </div>
              Merca<span className="text-[#00953B]">Go</span>
            </div>
            <p className="text-neutral-500 mt-2 max-w-xs" data-testid="footer-about-text">
              {fields?.about_text ||
                "Tu súper online — entregamos a tu familia en los campamentos saharauis."}
            </p>
            {(fields?.contact_email || fields?.contact_phone || fields?.whatsapp_link || socials.length > 0) && (
              <div className="mt-4 space-y-1 text-xs text-neutral-600">
                {fields?.contact_email && (
                  <div>
                    <a
                      href={`mailto:${fields.contact_email}`}
                      data-testid="footer-contact-email"
                      className="hover:text-[#00953B]"
                    >
                      {fields.contact_email}
                    </a>
                  </div>
                )}
                {fields?.contact_phone && (
                  <div data-testid="footer-contact-phone">{fields.contact_phone}</div>
                )}
                {fields?.whatsapp_link && (
                  <div>
                    <a
                      href={fields.whatsapp_link}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="footer-whatsapp-link"
                      className="hover:text-[#00953B]"
                    >
                      WhatsApp
                    </a>
                  </div>
                )}
                {socials.length > 0 && (
                  <div className="flex flex-wrap gap-3 pt-1" data-testid="footer-socials">
                    {socials.map((s, i) => (
                      <a
                        key={`${s.platform}-${i}`}
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`footer-social-${s.platform}`}
                        className="capitalize hover:text-[#00953B]"
                      >
                        {s.platform}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <nav
            data-testid="footer-legal-links"
            className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-xs text-neutral-600"
          >
            <Link data-testid="footer-link-aviso-legal" to="/legal/aviso_legal" className="hover:text-[#00953B]">
              Aviso legal
            </Link>
            <Link data-testid="footer-link-privacidad" to="/legal/privacidad" className="hover:text-[#00953B]">
              Privacidad
            </Link>
            <Link data-testid="footer-link-cookies" to="/legal/cookies" className="hover:text-[#00953B]">
              Cookies
            </Link>
            <Link data-testid="footer-link-terminos" to="/legal/terminos_y_condiciones" className="hover:text-[#00953B]">
              Términos y condiciones
            </Link>
            <Link data-testid="footer-link-faqs" to="/preguntas-frecuentes" className="hover:text-[#00953B]">
              Preguntas frecuentes
            </Link>
            <Link data-testid="footer-link-sobre-nosotros" to="/sobre-nosotros" className="hover:text-[#00953B]">
              Sobre nosotros
            </Link>
            <Link data-testid="footer-link-condiciones-envio" to="/condiciones-envio" className="hover:text-[#00953B]">
              Condiciones de envío
            </Link>
            <Link data-testid="footer-link-eliminar-cuenta" to="/eliminar-cuenta" className="hover:text-[#00953B]">
              Eliminar mi cuenta
            </Link>
          </nav>
        </div>
      </div>
      <div className="border-t border-neutral-200 py-4 text-center text-xs text-neutral-500">
        © {new Date().getFullYear()} MercaGo
      </div>
    </footer>
  );
}

function AppShell({ shop }) {
  return (
    <ShopProvider shop={shop}>
      <div className="min-h-screen flex flex-col bg-emerald-50 text-neutral-900">
        <Header />
        <MegaCategoriesPanel />
        <CartDrawer />
        <main className="flex-1">
          <Routes>
            {/* Phase 32 — shop-scoped storefront routes.
                Rendered at the mount point (`/es/*` or `/camp/*`), so the
                paths declared here are RELATIVE to that mount point.

                Phase 35 hotfix — literal routes first, catch-all `:id` last.
                React Router v7 does static-vs-dynamic ranking automatically
                so the order below is not strictly required, but we keep
                literals grouped at the top to make intent obvious and to
                protect us from future maintainers accidentally introducing
                a param that competes with `:id`. */}
            <Route index element={<Home />} />
            <Route path="tienda" element={<Tienda />} />
            <Route path="carrito" element={<CartPage />} />
            <Route path="categoria/:slug" element={<CategoryPage />} />
            <Route path="producto/:slug" element={<ProductPage />} />
            <Route path="buscar" element={<SearchPage />} />
            <Route path="checkout" element={<CheckoutPage />} />
            <Route path="checkout/success" element={<CheckoutSuccessPage />} />
            <Route path="pedido/:id" element={<OrderConfirmationPage />} />
            <Route path="destacados" element={<DestacadosPage />} />
            <Route path="nuevo" element={<NovedadesPage />} />
            <Route path="novedades" element={<NovedadesPage />} />
            <Route path="para-ti" element={<ParaTiPage />} />
            {/* Phase 33 hotfix — legacy /envio/{id} shipment URLs. When
                mounted under the `envio` AppShell (shop="envio"), matches
                any UUID landing at `/envio/{id}` and disambiguates via a
                backend probe (shipment vs order) before redirecting.
                Kept LAST so specific literals above are never shadowed. */}
            <Route path=":id" element={<LegacyEnvioIdRedirect />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </ShopProvider>
  );
}

/**
 * Shell for global (un-shopped) routes: legal, auth, envío-paquete, my-account,
 * my-orders, etc. Renders the header + footer so users don't feel "outside"
 * the app, but does NOT install a shop context — components that require one
 * must be defensive (`useShop()` may return null here).
 */
function GlobalShell() {
  return (
    <div className="min-h-screen flex flex-col bg-emerald-50 text-neutral-900">
      <Header />
      <MegaCategoriesPanel />
      <CartDrawer />
      <main className="flex-1">
        <Routes>
          <Route path="/entrar" element={<LoginPage />} />
          <Route path="/registro" element={<RegisterPage />} />
          <Route path="/olvide-contrasena" element={<OlvideContrasenaPage />} />
          <Route path="/restablecer-contrasena" element={<RestablecerContrasenaPage />} />
          <Route path="/enviar-paquete" element={<RequireAuth><EnviarPaqueteNuevoPage /></RequireAuth>} />
          <Route path="/enviar-paquete/nuevo" element={<RequireAuth><EnviarPaqueteNuevoPage /></RequireAuth>} />
          <Route path="/enviar-paquete/success" element={<RequireAuth><EnviarPaqueteSuccessPage /></RequireAuth>} />
          {/* Phase 33 — canonical shipment detail URL. Replaces the legacy
              `/envio/{id}` which now collides with the storefront prefix. */}
          <Route path="/envio-paquete/:id" element={<EnvioConfirmacionPage />} />
          <Route path="/envio/:id" element={<EnvioConfirmacionPage />} />
          <Route path="/legal/:slug" element={<LegalPage />} />
          <Route path="/preguntas-frecuentes" element={<FaqsPublic />} />
          <Route path="/sobre-nosotros" element={<AboutPage />} />
          <Route path="/condiciones-envio" element={<ShippingTermsPage />} />
          <Route path="/lista-inteligente" element={<ShoppingListPage />} />
          <Route path="/mis-envios" element={<RequireAuth><MisEnviosPage /></RequireAuth>} />
          <Route path="/mi-cuenta" element={<RequireAuth><AccountPage /></RequireAuth>} />
          <Route path="/mis-pedidos" element={<RequireAuth><MyOrdersPage /></RequireAuth>} />
          <Route path="/mis-habituales" element={<RequireAuth><FavoritesPage /></RequireAuth>} />
          {/* Phase 32 hotfix 2 — legacy un-prefixed order URL. Resolves the
              order's shop via the backend and redirects to
              `/{shop}/pedido/{id}` so old email links keep working. */}
          <Route path="/pedido/:id" element={<LegacyOrderRedirect />} />
          {/* Phase 32 — legacy un-prefixed URLs redirect via cookie or splash. */}
          <Route path="/producto/:slug" element={<LegacyShopRedirect subpath="/producto/:slug" />} />
          <Route path="/categoria/:slug" element={<LegacyShopRedirect subpath="/categoria/:slug" />} />
          <Route path="/buscar" element={<LegacyShopRedirect subpath="/buscar" />} />
          <Route path="/destacados" element={<LegacyShopRedirect subpath="/destacados" />} />
          <Route path="/nuevo" element={<LegacyShopRedirect subpath="/nuevo" />} />
          <Route path="/novedades" element={<LegacyShopRedirect subpath="/novedades" />} />
          <Route path="/para-ti" element={<LegacyShopRedirect subpath="/para-ti" />} />
          <Route path="/checkout" element={<LegacyShopRedirect subpath="/checkout" />} />
          {/* Phase 32 hotfix — Stripe's success/cancel redirects came back to
              `/checkout/success` without a shop prefix, which used to 404 after
              the storefront split. Bridge them via the cookie-aware redirect. */}
          <Route path="/checkout/success" element={<LegacyShopRedirect subpath="/checkout/success" />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function AppShellWithAdminGuard() {
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  if (path.startsWith("/admin")) return <AdminRoutes />;
  return <GlobalShell />;
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <FavoritesProvider>
              <SettingsProvider>
                <ScrollToTopOnRouteChange />
                <Routes>
                  <Route path="/admin/*" element={<AdminRoutes />} />
                  {/* Phase 47 — public delivery confirmation flow. Rendered
                      outside the storefront shell so transporters get a
                      focused UI (no header, cart, WhatsApp FAB, footer). */}
                  <Route path="/deliver/gracias" element={<DeliverThanks />} />
                  <Route path="/deliver/:id" element={<DeliverPage />} />
                  {/* Página pública standalone de borrado de cuenta —
                      renderizada FUERA del `GlobalShell` para tener layout
                      minimal (solo logo, sin navbar completo) y funcionar
                      bien tanto en web como en webview de la app móvil. */}
                  <Route path="/eliminar-cuenta" element={<DeleteAccountPage />} />
                  {/* Phase 32 — root redirects to the last-visited shop (or default). */}
                  <Route path="/" element={<RootShopRedirect />} />
                  {/* Shop-scoped mounts. */}
                  <Route path="/envio/*" element={<AppShell shop="envio" />} />
                  {/* Phase 35 — `/local/*` storefront hidden from the UI. Any
                      legacy link is bridged to the `/envio/*` equivalent so
                      nothing 404s. Backend keeps the shop system in the
                      model; only the UI shortcut is gone. */}
                  <Route path="/local/*" element={<LocalToEnvioRedirect />} />
                  {/* Everything else — legal, auth, envío paquete, cuenta. */}
                  <Route path="/*" element={<GlobalShell />} />
                </Routes>
                <WhatsAppFab />
                <Toaster position="top-right" richColors />
              </SettingsProvider>
            </FavoritesProvider>
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}
